// « Nouvel éditeur mobile » — a bottom sheet that DOCKS under the album
// instead of covering it: it sits in the page's column between the album and
// the tab bar, so the album shrinks above it and is never hidden. Swipe the
// handle (or the content, once scrolled to the top) down to close.
import { useRef, useState } from 'react';
import { M2 } from './theme';

export function MobileSheet({
  title,
  onClose,
  children,
  maxHeight = '52%',
  height,
  actions,
  sheetKey,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** CSS height cap — a share of the editor frame, the album keeps the rest. */
  maxHeight?: string;
  /** A FIXED height instead (sheets whose content measures its box, e.g. the
   *  virtualised photo grid). */
  height?: string;
  /** Extra buttons in the header row, left of « Fermer ». */
  actions?: React.ReactNode;
  /** Identifies the sheet for tests (data-m2-sheet). */
  sheetKey: string;
}) {
  const [drag, setDrag] = useState(0);
  // drag the handle UP = taller sheet (85 %); down from there = back to its size
  const [expanded, setExpanded] = useState(false);
  const upRef = useRef(0);
  const startRef = useRef<{ x: number; y: number; fromContent: boolean } | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  /** A drag that starts inside something already scrolled (the photo grid,
   *  the sheet body) is that thing's scroll, never a « close ». */
  const scrolledAbove = (target: EventTarget | null) => {
    for (let n = target as HTMLElement | null; n && n !== bodyRef.current?.parentElement; n = n.parentElement) {
      if (n.scrollTop > 0) return true;
    }
    return false;
  };
  const begin = (x: number, y: number, fromContent: boolean, target: EventTarget | null) => {
    if (fromContent && scrolledAbove(target)) { startRef.current = null; return; }
    startRef.current = { x, y, fromContent };
  };
  const move = (x: number, y: number) => {
    const s = startRef.current;
    if (!s) return;
    const dy = y - s.y;
    const dx = x - s.x;
    // sideways = the page strip scrolling or a reorder, never a close
    if (Math.abs(dx) > Math.abs(dy)) { if (Math.abs(dx) > 10) { startRef.current = null; setDrag(0); } return; }
    if (dy <= 0) { setDrag(0); if (!s.fromContent) upRef.current = -dy; return; }
    // content drags only take over once clearly downward (a scroll otherwise)
    if (s.fromContent && dy < 14) return;
    setDrag(dy);
  };
  const end = () => {
    const closing = drag > 70;
    const growing = upRef.current > 40;
    upRef.current = 0;
    startRef.current = null;
    setDrag(0);
    if (growing) { setExpanded(true); return; }
    if (closing) { if (expanded) setExpanded(false); else onClose(); }
  };

  return (
    <section
      data-m2-sheet={sheetKey}
      role="region"
      aria-label={title}
      className="m2-sheet shrink-0 flex flex-col relative z-20"
      style={{
        maxHeight: expanded ? '85%' : height ? undefined : maxHeight,
        height: expanded ? '85%' : height,
        minHeight: 0,
        background: M2.surface,
        borderTop: `1px solid ${M2.gold}33`,
        borderRadius: '16px 16px 0 0',
        boxShadow: '0 -6px 18px rgba(31,41,55,0.10)',
        transform: drag ? `translateY(${drag}px)` : undefined,
        transition: drag ? 'none' : 'transform 160ms ease',
        color: M2.ink,
      }}
    >
      <div
        className="shrink-0 flex items-center gap-1 pl-4 pr-1 pt-1"
        style={{ touchAction: 'none' }}
        onTouchStart={(e) => begin(e.touches[0].clientX, e.touches[0].clientY, false, e.target)}
        onTouchMove={(e) => move(e.touches[0].clientX, e.touches[0].clientY)}
        onTouchEnd={end}
        onTouchCancel={end}
      >
        {/* the grab bar: drag it up (or tap it) for a taller sheet */}
        <div aria-hidden onClick={() => setExpanded((v) => !v)}
          className="absolute left-1/2 -translate-x-1/2 top-0 w-16 h-4 flex items-start justify-center pt-1.5">
          <span className="block w-10 h-1 rounded-full" style={{ background: `${M2.gold}55` }} />
        </div>
        <h2 className="flex-1 min-w-0 truncate font-semibold pt-1.5" style={{ fontSize: 15 }}>{title}</h2>
        {actions}
        <button
          type="button"
          onClick={onClose}
          className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center active:scale-95"
          aria-label="Fermer"
          title="Fermer"
          style={{ color: M2.muted }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden>
            <path d="M6 6l12 12M6 18L18 6" />
          </svg>
        </button>
      </div>
      <div
        ref={bodyRef}
        className="m2-sheet-body flex-1 min-h-0 overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: 'touch' }}
        onTouchStart={(e) => begin(e.touches[0].clientX, e.touches[0].clientY, true, e.target)}
        onTouchMove={(e) => move(e.touches[0].clientX, e.touches[0].clientY)}
        onTouchEnd={end}
        onTouchCancel={end}
      >
        {children}
      </div>
    </section>
  );
}
